import { apiPath } from '../../constants';
import { axios, apiRequestOptions, transformHttpResponse, errorRequestHandler } from 'utils';

export const OPERATIONS_REQUEST = 'OPERATIONS_REQUEST';
export const OPERATIONS_SUCCESS = 'OPERATIONS_SUCCESS';
export const OPERATIONS_FAILURE = 'OPERATIONS_FAILURE';

/**
 * The tatami floor of ONE competition.
 *
 * The response is already scoped by the backend: a chief secretary receives
 * every mat, a tatami secretary only the mats they were put on. Nothing here
 * filters — if the client had to hide rows, the data would already have been
 * on the wire.
 */
export const fetchOperations = competitionId => dispatch => {
    if (!competitionId) {
        return Promise.resolve();
    }

    dispatch({ type: OPERATIONS_REQUEST, competitionId });

    return axios(apiRequestOptions(apiPath.OPERATIONS.LIST, {}, { params: { competitionId } }))
        .then(transformHttpResponse)
        .then(response => dispatch({ type: OPERATIONS_SUCCESS, competitionId, data: response.data }))
        .catch(error => {
            dispatch({ type: OPERATIONS_FAILURE, competitionId });
            dispatch(errorRequestHandler(error));
        });
};

export const OPERATIONS_SECRETARIES_SUCCESS = 'OPERATIONS_SECRETARIES_SUCCESS';

/**
 * The people this competition's mats may be handed to.
 *
 * Fetched once when the floor opens rather than carried on the ten-second
 * poll: the list changes about once per competition. The backend decides the
 * set — it is the competition's own secretaries, the same set the assignment
 * endpoint validates against — so nothing here filters it.
 *
 * ROLE_MAIN_SECRETARY only, so a mat secretary's 403 is expected and must not
 * surface as an error toast on a page they are otherwise allowed to use.
 */
export const fetchAssignableSecretaries = competitionId => dispatch => {
    if (!competitionId) {
        return Promise.resolve();
    }

    return axios(apiRequestOptions(apiPath.OPERATIONS.ASSIGNABLE_SECRETARIES, {}, { params: { competitionId } }))
        .then(transformHttpResponse)
        .then(response => dispatch({
            type: OPERATIONS_SECRETARIES_SUCCESS,
            competitionId,
            data: response.data,
            allowed: true,
        }))
        // `allowed` is the whole point of catching here. A mat secretary gets
        // 403 and must see no control; a chief secretary whose competition has
        // nobody attached yet gets 200 and an empty list, and must see the
        // control WITH an explanation. Collapsing both to "empty" made the
        // second case indistinguishable from the first — the control simply
        // vanished, with nothing on screen saying why.
        .catch(() => dispatch({
            type: OPERATIONS_SECRETARIES_SUCCESS,
            competitionId,
            data: [],
            allowed: false,
        }));
};

export const OPERATIONS_JUDGES_SUCCESS = 'OPERATIONS_JUDGES_SUCCESS';

/**
 * The competition's judges a mat may be given as referee or arbiter (канса)
 * (17 Oct 2026). Like the secretaries: once per floor, ROLE_MAIN_SECRETARY
 * only — anybody else stores an empty list and sees no control.
 */
export const fetchAssignableJudges = competitionId => dispatch => {
    if (!competitionId) {
        return Promise.resolve();
    }

    return axios(apiRequestOptions(apiPath.OPERATIONS.ASSIGNABLE_JUDGES, {}, { params: { competitionId } }))
        .then(transformHttpResponse)
        .then(response => dispatch({ type: OPERATIONS_JUDGES_SUCCESS, competitionId, data: response.data }))
        .catch(() => dispatch({ type: OPERATIONS_JUDGES_SUCCESS, competitionId, data: [] }));
};
